/**
 * Assign one PSIRT team to many products at once — the usual first step, since
 * most manufacturers run a single PSIRT for the whole portfolio. Existing
 * responsibility cards keep their owner and support period; only the team
 * reference changes. Products without a card get one.
 */

import { useMemo, useState, type ReactNode } from 'react';

import Chip from '../../components/Chip';
import { Modal } from '../../components/Modal';
import Spinner from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { createCmdbCard, errorMessage, updateCmdbCard } from '../../lib/api';
import type { PsirtProductRow, PsirtTeam } from '../../lib/types';

export interface AssignTeamModalProps {
  rows: readonly PsirtProductRow[];
  teams: readonly PsirtTeam[];
  onClose: () => void;
  onSaved: () => void;
}

export function AssignTeamModal({ rows, teams, onClose, onSaved }: AssignTeamModalProps): ReactNode {
  const toast = useToast();
  const [teamId, setTeamId] = useState<number | null>(teams.length === 1 ? (teams[0]?.id ?? null) : null);
  const [selected, setSelected] = useState<ReadonlySet<number>>(
    () => new Set(rows.filter((row) => row.team_id === null).map((row) => row.produkt_id)),
  );
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.produkt.localeCompare(b.produkt, 'de-DE', { numeric: true })),
    [rows],
  );
  const targets = sorted.filter((row) => selected.has(row.produkt_id) && row.team_id !== teamId);

  const toggle = (id: number): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  async function apply(): Promise<void> {
    if (teamId === null || targets.length === 0) return;
    setSaving(true);
    setDone(0);
    const failed: string[] = [];
    for (const row of targets) {
      try {
        if (row.verantwortung_id !== null) {
          await updateCmdbCard('ProduktVerantwortung', row.verantwortung_id, { PsirtTeamID: teamId });
        } else {
          await createCmdbCard('ProduktVerantwortung', { ProduktTypeID: row.produkt_id, PsirtTeamID: teamId });
        }
      } catch (err) {
        failed.push(`${row.produkt}: ${errorMessage(err)}`);
      }
      setDone((count) => count + 1);
    }
    setErrors(failed);
    setSaving(false);
    const ok = targets.length - failed.length;
    if (ok > 0) toast.success(`${ok} Produkt${ok === 1 ? '' : 'e'} zugeordnet.`);
    if (failed.length === 0) onSaved();
  }

  const team = teams.find((entry) => entry.id === teamId) ?? null;

  return (
    <Modal
      title="PSIRT-Team zuweisen"
      subtitle="Nur das Team wird gesetzt. Owner, Supportzeitraum und Kategorie bleiben erhalten."
      onClose={errors.length > 0 ? onSaved : onClose}
      dismissable={!saving}
      footer={
        <>
          <span className="spacer" />
          <button type="button" onClick={errors.length > 0 ? onSaved : onClose} disabled={saving}>
            {errors.length > 0 ? 'Schließen' : 'Abbrechen'}
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => void apply()}
            disabled={saving || teamId === null || targets.length === 0}
          >
            {saving ? (
              <Spinner variant="on-accent" label={`${done}/${targets.length} gespeichert…`} />
            ) : (
              `${targets.length} Produkt${targets.length === 1 ? '' : 'e'} zuweisen`
            )}
          </button>
        </>
      }
    >
      <div className="stack psirt-assign">
        {errors.length > 0 ? (
          <div className="banner error" role="alert">
            <strong>Nicht alle Zuordnungen wurden gespeichert:</strong>
            <ul>
              {errors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="psirt-assign-team">PSIRT-Team</label>
          <select
            id="psirt-assign-team"
            value={teamId ?? ''}
            disabled={saving}
            onChange={(event) => setTeamId(event.target.value === '' ? null : Number(event.target.value))}
          >
            <option value="">— wählen —</option>
            {teams.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
          </select>
          {team && team.state !== 'ok' ? (
            <span className="hint">
              Im Team sind noch {team.gesamt - team.erfuellt} Anforderung(en) offen. Das betrifft jedes
              zugewiesene Produkt.
            </span>
          ) : null}
        </div>

        <div className="psirt-assign-head">
          <span className="field-label">Produkte</span>
          <span className="row">
            <button
              type="button"
              className="ghost small"
              disabled={saving}
              onClick={() => setSelected(new Set(rows.map((row) => row.produkt_id)))}
            >
              Alle
            </button>
            <button
              type="button"
              className="ghost small"
              disabled={saving}
              onClick={() => setSelected(new Set(rows.filter((row) => row.team_id === null).map((row) => row.produkt_id)))}
            >
              Nur ohne Team
            </button>
            <button type="button" className="ghost small" disabled={saving} onClick={() => setSelected(new Set())}>
              Keine
            </button>
          </span>
        </div>
        <ul className="psirt-assign-list">
          {sorted.map((row) => {
            const already = teamId !== null && row.team_id === teamId;
            return (
              <li key={row.produkt_id}>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={selected.has(row.produkt_id)}
                    disabled={saving}
                    onChange={() => toggle(row.produkt_id)}
                  />
                  <span>
                    <strong>{row.produkt}</strong>
                    {row.modellreihe ? <span className="muted"> · {row.modellreihe}</span> : null}
                  </span>
                </label>
                {already ? (
                  <Chip tone="good">bereits zugeordnet</Chip>
                ) : row.team ? (
                  <Chip title="Wird umgehängt">{row.team}</Chip>
                ) : (
                  <Chip tone="warn">ohne Team</Chip>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </Modal>
  );
}

export default AssignTeamModal;
